import React from 'react';
import { notification } from 'antd';
import '../../IntegrationBaseComponent.scss';
import { FacebookAdsIntegrationsHeader } from './FacebookAdsIntegrationsHeader';
import {
  Grid,
  GridLabels,
  GridRow,
} from '../../../../../../../../common/components/Grid/Grid';
import {
  ComponentDataItem,
  DefaultCell,
} from '../../../../../../contacts/components/ContactsTable/ContactTable';
import { TableLabel } from '../../../../../../../../common/components/TableGrid/TableGrid';
import { landmineNames } from '@mm/constants/landmine-names';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { ReactComponent as FacebookIcon } from 'assets/facebook-circle.svg';

import { useSelector } from 'react-redux';
import { selectFacebookAdAudiencesByAdAccount } from '@mm/state/selectors/adAudiencesSelector';
import AdAccountModel from '@mm/state/model/AdAccountModel';
import { StoreState } from '@mm/state/store-interfaces';
import css from './FacebookAdsIntegrations.module.css';
import classNames from 'classnames';
import { CAPIConfigurations } from './CAPIConfigurations';
import fbPeopleSrc from 'assets/facebook-people.svg';
import fbWebsiteSrc from 'assets/facebook-website.svg';
import { Button, Tooltip } from '@mm/components';
import { LinkOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { useConnectionsTableActions } from '@mm/state/modules/connectionsTableState/actions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { ButtonColor } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { FacebookAdsConnect } from './FacebookAdsConnect';
import { useFBConnect } from './use-fb-connect';
import { useModalActions } from '@mm/state/modules/modalsState/actions';

type Props = {
  cellData: ComponentDataItem[];
  name?: string;
  adAccounts?: AdAccountModel[];
  selectedAdAccount?: AdAccountModel;
  onSelectedAdAccountChange?: (adAccount: AdAccountModel) => void;
  onPageConnectSuccess?: () => void;
};

export const FacebookAdsIntegration = (props: Props) => {
  const {
    cellData,
    adAccounts = [],
    name,
    selectedAdAccount,
    onSelectedAdAccountChange,
    onPageConnectSuccess,
  } = props;
  const entities = useSelector((state: StoreState) =>
    selectFacebookAdAudiencesByAdAccount(state, props?.selectedAdAccount?.id),
  );
  const subscriptionFeatures = useSelector(
    (state: StoreState) => state.featureFlags.subscriptionFeatureFlags,
  );

  const gridTemplateColumns = `20% 20% 20% 15% 15% 10%`;

  const { setActiveIntegration } = useConnectionsTableActions();
  const { removeToken, hasFBConnection, hasFBOauthTokens } = useFBConnect();
  const { openNewBillingModal2023 } = useModalActions();

  const { showSlideoutMenu } = useViewActions();

  const addNew = async () => {
    showSlideoutMenu('fbAdsIntegrationEditor');
  };

  const sectionProps = {
    landmineName: landmineNames?.INTEGRATIONS_FACEBOOK_ADS,
  };

  const onClickAddNew = () => {
    if (!subscriptionFeatures.includes('fbRestore')) {
      openNewBillingModal2023(sectionProps);
      return;
    }
    addNew();
    setActiveIntegration(name);
  };

  const onTokenRemoveClick = async () => {
    await removeToken();

    notification.success({
      message: 'FB Oauth token has been disabled',
    });
  };

  const headerComponent = (
    <FacebookAdsIntegrationsHeader
      count={entities.length}
      name={name}
      adAccounts={adAccounts}
      selectedAdAccount={selectedAdAccount}
      onSelectedAdAccountChange={onSelectedAdAccountChange}
      onPageConnectSuccess={onPageConnectSuccess}
    />
  );

  return (
    <div>
      <BaseIntegration
        openOnMount={window?.location?.href?.includes('restore')}
        integrationType="facebook_ads_audience"
        titleComponent={
          <BaseIntegrationHeader
            title="Facebook"
            icon={<FacebookIcon />}
            headerRightComponent={headerComponent}
            css={{
              container: classNames(
                css.headerAccordion,
                'integrations__header-container',
              ),
              icon: 'integrations__header-icon-facebook',
            }}
            {...sectionProps}
          />
        }
      >
        {!hasFBConnection ? (
          <div className={css.empty}>
            <div className={css.iconContainer}>
              <img
                className={css.icon}
                src={fbWebsiteSrc}
                alt="Facebook Page"
              />
            </div>

            <FacebookAdsConnect className={css.connectBtn} />
          </div>
        ) : (
          <>
            <div className={css.container}>
              <div className={css.header}>
                <span className={css.title}>
                  Custom Audience Sync
                  <Tooltip
                    title="Send your website visitor data as a custom audience in your
                    audience manager."
                    placement="bottom"
                  >
                    <QuestionCircleOutlined className={css.infoIcon} />
                  </Tooltip>
                </span>
                <div className={css.actions}>
                  <div className={css.countInfo}>
                    Synced Audiences
                    <LinkOutlined className={css.countIcon} />
                    <div className={css.countText}>{entities.length}</div>
                  </div>

                  <Button
                    css={{ container: css.actionBtn }}
                    color={ButtonColor.secondaryBlue}
                    onClick={onClickAddNew}
                  >
                    Create Facebook Audience
                  </Button>
                  {hasFBOauthTokens && (
                    <Button onClick={onTokenRemoveClick}>
                      Disable FB Token
                    </Button>
                  )}
                </div>
              </div>

              {!entities.length ? (
                <div className={css.empty}>
                  <div className={css.iconContainer}>
                    <img
                      className={css.icon}
                      src={fbPeopleSrc}
                      alt="Facebook People"
                    />
                  </div>
                  <Button
                    css={{ container: css.ctaBtn }}
                    color={ButtonColor.blue}
                    onClick={onClickAddNew}
                  >
                    Create Facebook Audience
                  </Button>
                </div>
              ) : (
                <Grid className={classNames(css.table, 'integrations-grid')}>
                  <>
                    <GridLabels
                      className={css.tableHeader}
                      gridTemplateColumns={gridTemplateColumns}
                    >
                      {cellData.map(cell => (
                        <TableLabel
                          key={
                            (cell.keyName as string) || cell.label?.toString()
                          }
                          dataItem={cell}
                          orderByAtTheEnd
                        />
                      ))}
                    </GridLabels>

                    <div className={css.tableRows}>
                      {entities.map(item => (
                        <GridRow
                          key={item.id}
                          gridTemplateColumns={gridTemplateColumns}
                        >
                          {cellData.map(({ Cell, keyName }) =>
                            Cell ? (
                              <Cell key={keyName} item={item} />
                            ) : (
                              <DefaultCell
                                key={keyName}
                                keyName={keyName as string}
                                item={item}
                              />
                            ),
                          )}
                        </GridRow>
                      ))}
                    </div>
                  </>
                </Grid>
              )}
            </div>

            <CAPIConfigurations />
          </>
        )}
      </BaseIntegration>
    </div>
  );
};
